import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

interface MfaChallengeScreenProps {
  /** Where to navigate after the challenge succeeds. */
  redirectTo: string;
}

/**
 * Returning-admin MFA challenge. Accepts either a 6-digit TOTP code or a
 * 10-character recovery code — the server treats both the same, so the UI
 * is a single free-form field with a tiny helper link that clarifies
 * recovery codes are accepted here.
 */
export default function MfaChallengeScreen({
  redirectTo,
}: MfaChallengeScreenProps) {
  const navigate = useNavigate();

  const error = useAuthStore((s) => s.error);
  const token = useAuthStore((s) => s.token);
  const adminUser = useAuthStore((s) => s.adminUser);
  const submitMfaCode = useAuthStore((s) => s.submitMfaCode);
  const logout = useAuthStore((s) => s.logout);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showRecoveryHint, setShowRecoveryHint] = useState(false);

  // Success — authStore moved us to authenticated, bounce out.
  useEffect(() => {
    if (token && adminUser) {
      navigate(redirectTo, { replace: true });
    }
  }, [token, adminUser, navigate, redirectTo]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = code.trim();
    if (!value) return;
    setSubmitting(true);
    try {
      await submitMfaCode(value);
    } catch {
      // Error recorded in store — user stays on this screen, token preserved.
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-100 px-4"
      data-testid="mfa-challenge-screen"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white rounded-lg shadow p-6 space-y-5"
        aria-labelledby="mfa-challenge-title"
      >
        <div className="text-center space-y-1">
          <h1
            id="mfa-challenge-title"
            className="text-xl font-semibold text-slate-900"
          >
            Verification required
          </h1>
          <p className="text-sm text-slate-500">
            Enter the code from your authenticator app.
          </p>
        </div>

        <label className="block text-sm">
          <span className="sr-only">Authentication code</span>
          <input
            type="text"
            inputMode="text"
            autoComplete="one-time-code"
            autoFocus
            required
            maxLength={10}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={submitting}
            placeholder="6-digit or recovery code"
            data-testid="mfa-challenge-code"
            className="block w-full rounded border border-slate-300 px-3 py-2 font-mono tracking-widest text-center text-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
          />
        </label>

        <div className="text-xs text-slate-500 text-center">
          <button
            type="button"
            onClick={() => setShowRecoveryHint((v) => !v)}
            data-testid="mfa-challenge-recovery-toggle"
            className="underline text-slate-600 hover:text-slate-900"
          >
            I lost my authenticator → use a recovery code
          </button>
          {showRecoveryHint && (
            <p
              data-testid="mfa-challenge-recovery-hint"
              className="mt-2 text-slate-500"
            >
              Enter any one of your 10-character recovery codes in the field
              above. Each code works once.
            </p>
          )}
        </div>

        {error && (
          <div
            role="alert"
            data-testid="mfa-challenge-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={submitting || !code.trim()}
            data-testid="mfa-challenge-verify"
            className="flex-1 rounded bg-slate-800 text-white py-2 font-medium hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            data-testid="mfa-challenge-cancel"
            className="rounded border border-slate-300 text-slate-700 px-3 py-2 text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
